import { useNavigate, useParams } from "@tanstack/react-router";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useOTRuleDetailQuery, useUpdateOTRuleMutation } from "../api/queries";
import { OTRuleForm } from "../ui/OTRuleForm";
import type { UpdateOTRuleDTO } from "@/modules/hr/overtime/domain/types";
import { Button, toast } from "@devhop/ui";
import { ArrowLeft, HelpCircle, Loader2 } from "lucide-react";
import { OTRuleEditTour, useOTRuleEditTour } from "../tour/OTRuleEditTour";

export function OTRuleEditPage() {
  const { id } = useParams({ strict: false });
  const nav = useNavigate();
  const { data: rule, isLoading, error } = useOTRuleDetailQuery(id);
  const updateMutation = useUpdateOTRuleMutation();
  const tour = useOTRuleEditTour();

  const handleSubmit = (values: UpdateOTRuleDTO) => {
    if (!id) return;
    updateMutation.mutate(
      { id, dto: values },
      {
        onSuccess: () => {
          toast.success("ບັນທຶກການແກ້ໄຂສຳເລັດ");
          nav({ to: "/app/hr/overtime/rules/$id", params: { id } });
        },
        onError: (err) => {
          toast.error(err?.message ?? "ເກີດຂໍ້ຜິດພາດ");
        },
      },
    );
  };

  if (isLoading || (!rule && !error)) {
    return (
      <>
        <Header />
        <Main>
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </Main>
      </>
    );
  }

  if (error || !rule) {
    return (
      <>
        <Header />
        <Main>
          <div className="mx-auto w-full max-w-4xl py-4">
            <p className="text-destructive">ບໍ່ພົບຂໍ້ກຳນົດ OT.</p>
            <Button
              variant="outline"
              className="mt-4"
              onClick={() =>
                nav({
                  to: "/app/hr/overtime/rules",
                  search: {
                    offset: 0,
                    limit: 20,
                    dayType: undefined,
                    isActive: undefined,
                    search: undefined,
                  },
                })
              }
            >
              <ArrowLeft className="h-4 w-4" />
              ກັບຄືນ
            </Button>
          </div>
        </Main>
      </>
    );
  }

  const handleBack = () => {
    nav({ to: "/app/hr/overtime/rules/$id", params: { id: rule.id } });
  };

  return (
    <>
      <Header />
      <Main>
        <OTRuleEditTour run={tour.run} onCallback={tour.handleJoyrideCallback} />
        <div className="mb-6 flex items-center justify-between gap-2" data-tourid="ot-rule-edit-back">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="font-semibold text-2xl">ແກ້ໄຂຂໍ້ກຳນົດ OT</h1>
              <Button
                variant="ghost"
                size="icon"
                className="size-7 rounded-full"
                onClick={tour.startTour}
                title="ເລີ່ມທົດສອບການນຳທາງ"
                aria-label="ເລີ່ມທົດສອບການນຳທາງ"
              >
                <HelpCircle className="size-5" />
              </Button>
            </div>
            <p className="text-muted-foreground text-sm">{rule.name}</p>
          </div>
          <Button variant="ghost" size="sm" onClick={handleBack} className="gap-2">
            <ArrowLeft className="h-4 w-4" />
            ກັບໄປລາຍການ
          </Button>
        </div>

        <div className="mx-auto w-full max-w-4xl space-y-6" data-tourid="ot-rule-edit-form">
          <OTRuleForm
            mode="edit"
            rule={rule}
            onSubmit={handleSubmit}
            onCancel={handleBack}
            isSubmitting={updateMutation.isPending}
          />
        </div>
      </Main>
    </>
  );
}
